import cn from "classnames";
import Pagination from "components/shared/reactTable/pagination";
import scrollbarWidth from "components/shared/scrollbarWidth";
import { splitKeyFromProps } from "helpers/reactTableProps";
import { REPORT_TYPES } from "helpers/reports";
import React, { Fragment, useEffect, useRef, useState } from "react";
import {
    useExpanded,
    useFilters,
    useFlexLayout,
    useGroupBy,
    usePagination,
    useResizeColumns,
    useSortBy,
    useTable,
} from "react-table";
import { VariableSizeList } from "react-window";

const defaultPropGetter = () => ({});

const Table = (props) => {
    const {
        renderRowSubComponent,
        isPrintForm,
        withoutPagination,
        withoutFooter = false,
        getRowProps = defaultPropGetter,
        filterProps: { storeColumnWidth, setTableState, setPrintTableState, tableState },
    } = props;

    const instance = useTable(
        {
            ...props,
            paginateExpandedRows: false,
            initialState: {
                ...props.initialState,
                ...tableState,
                pageIndex: tableState.pageIndex ?? 0,
                pageSize: isPrintForm ? 9999 : (tableState.pageSize ?? 30),
            },
        },
        useResizeColumns,
        useFlexLayout,
        useFilters,
        useGroupBy,
        useSortBy,
        useExpanded,
        usePagination,
    );

    const [tbodyHeight, setTbodyHeight] = useState(0);
    const [tbodyWidth, setTbodyWidth] = useState(0);

    const scrollBarSize = React.useMemo(() => scrollbarWidth(), []);

    const { filters, expanded, sortBy, columnResizing, pageIndex, pageSize } = instance.state;
    useEffect(() => {
        storeColumnWidth(columnResizing);
    }, [columnResizing]);
    useEffect(() => {
        const currentTableState = { filters, expanded, sortBy, pageIndex, pageSize };
        setTableState(currentTableState);
        setPrintTableState?.({ filters, sortBy });
    }, [filters, expanded, sortBy, pageIndex, pageSize, setTableState, setPrintTableState]);

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        footerGroups,
        totalColumnsWidth,
        prepareRow,
        page,
        preGroupedRows,
        pageOptions,
        gotoPage,
        setPageSize,
        toggleAllRowsExpanded,
        state,
    } = instance;
    useEffect(() => {
        props.onFilteredRowsChange?.(preGroupedRows.map((row) => row.original));
    }, [preGroupedRows, props.onFilteredRowsChange]);
    useEffect(() => {
        if (!isPrintForm) {
            return;
        }
        const timeoutId = setTimeout(() => toggleAllRowsExpanded(true), 0);
        return () => clearTimeout(timeoutId);
    }, [isPrintForm, toggleAllRowsExpanded]);
    const listEl = useRef();
    useEffect(() => {
        listEl.current?.resetAfterIndex(0);
    }, [Object.keys(state.expanded).length]);

    useEffect(() => {
        const tableHeight = document.getElementsByClassName("r-table table-sm")[0]?.offsetHeight || 0;
        const thead = document.getElementsByClassName("r-table__thead")[0]?.offsetHeight || 0;
        const tfoot = document.getElementsByClassName("r-table__tfoot")[0]?.offsetHeight || 0;
        setTbodyHeight(tableHeight - thead - tfoot - 28);

        const tbody = document.getElementsByClassName("r-table__thead")[0]?.offsetWidth || 0;
        const width = totalColumnsWidth + scrollBarSize < tbody ? tbody : totalColumnsWidth + scrollBarSize;
        setTbodyWidth(width);
    }, [totalColumnsWidth]);
    const totalItems = preGroupedRows.length;
    const rangeStart = totalItems === 0 ? 0 : pageIndex * pageSize + 1;
    const rangeEnd = Math.min((pageIndex + 1) * pageSize, totalItems);

    useEffect(() => {
        props.onPaginationInfoChange?.({ rangeStart, rangeEnd, totalItems });
    }, [rangeStart, rangeEnd, totalItems, props.onPaginationInfoChange]);

    useEffect(() => {
        if (pageOptions.length > 0 && pageIndex >= pageOptions.length) {
            gotoPage(pageOptions.length - 1);
        }
    }, [pageIndex, pageOptions.length, gotoPage]);

    const paginationProps = {
        page: pageIndex,
        pages: pageOptions.length,
        pageSize,
        pageIndex,
        onPageChange: gotoPage,
        onPageSizeChange: setPageSize,
        pageSizeOptions: [30, 50, 100],
        showRange: props.showRange,
        totalItems,
    };
    const iconClass = (column, direction, baseClass) =>
        cn(baseClass, {
            "r-table__sort-icon--active":
                column.isSorted && (column.isSortedDesc ? direction === "desc" : direction === "asc"),
            "r-table__sort-icon--disabled":
                column.isSorted && (column.isSortedDesc ? direction === "asc" : direction === "desc"),
        });

    const RenderRow = React.useCallback(
        ({ index, style }) => {
            const row = page[index];
            return rowItem(row, index, style);
        },
        [prepareRow, page, totalColumnsWidth],
    );

    const rowItem = (row, index, style = {}) => {
        prepareRow(row);
        const { key: rowKey, props: rowProps } = splitKeyFromProps(row.getRowProps({ ...getRowProps(row), style }));
        return (
            <Fragment key={rowKey ?? index}>
                <div className="tr" {...rowProps}>
                    {row.cells.map((cell, i) => {
                        const { cellAdditionalProps, IsGrouped, IsAggregated } = cell.column;
                        const cellProps = cell.getCellProps(cellAdditionalProps);
                        const isGrouped = IsGrouped ? IsGrouped(cell) : cell.isGrouped;
                        const isAggregated = IsAggregated
                            ? IsAggregated(cell) && cell.isPlaceholder
                            : cell.isAggregated;
                        cellProps.className = cn("td", {
                            [cellProps.className]: !!cellProps.className,
                            "position-sticky bg-light": cell.column.sticky,
                        });
                        const { key: cellKey, props: cellPropsWithoutKey } = splitKeyFromProps(cellProps);
                        return (
                            <div
                                key={cellKey ?? i}
                                {...cellPropsWithoutKey}
                                style={{
                                    ...cellPropsWithoutKey.style,
                                    ...(cell.column.sticky
                                        ? {
                                              left: `${row.cells.slice(0, i).reduce((sum, cell) => sum + (cell.column.width || 0), 0)}px`,
                                          }
                                        : {}),
                                }}
                            >
                                {isGrouped ? (
                                    <div {...row.getToggleRowExpandedProps({ title: "", className: "d-flex" })}>
                                        <i
                                            className={cn("r-item__expander", {
                                                "icon-uEAAE-angle-right-solid": !row.isExpanded,
                                                "icon-uEA61-angle-down": row.isExpanded,
                                            })}
                                        />
                                        {cell.render("Cell")}
                                    </div>
                                ) : isAggregated ? (
                                    cell.render("Aggregated")
                                ) : cell.isPlaceholder ? null : (
                                    cell.render("Cell")
                                )}
                            </div>
                        );
                    })}
                </div>
                {row.isExpanded &&
                    isPrintForm &&
                    row.depth === 0 &&
                    renderRowSubComponent &&
                    renderRowSubComponent(row, isPrintForm)}
            </Fragment>
        );
    };

    const rowHeights = page.map((row) => (props.getRowHeight ? props.getRowHeight(row) : 32));

    const getItemSize = (index) => rowHeights[index] || 0;

    return (
        <>
            <div {...getTableProps()} className="r-table table-sm r-table__reports">
                <div className="r-table__thead">
                    {headerGroups.map((headerGroup, i) => {
                        const { key: headerGroupKey, props: headerGroupProps } = splitKeyFromProps(
                            headerGroup.getHeaderGroupProps(),
                        );
                        return (
                            <div key={headerGroupKey ?? i} {...headerGroupProps} className="tr">
                                {headerGroup.headers.map((column, i) => {
                                    const headerProps = column.getHeaderProps(column.cellAdditionalProps);
                                    headerProps.className = cn("th", {
                                        [headerProps.className]: !!headerProps.className,
                                        "position-sticky zi-9": column.sticky,
                                    });
                                    const { key: headerKey, props: headerPropsWithoutKey } =
                                        splitKeyFromProps(headerProps);

                                    return (
                                        <div
                                            key={headerKey ?? i}
                                            {...headerPropsWithoutKey}
                                            style={{
                                                ...headerPropsWithoutKey.style,
                                                ...(column.sticky
                                                    ? {
                                                          left: `${headerGroup.headers.slice(0, i).reduce((sum, column) => sum + (column.width || 0), 0)}px`,
                                                      }
                                                    : {}),
                                            }}
                                        >
                                            <div
                                                {...column.getSortByToggleProps({ title: "" })}
                                                className={`r-table__column-title ${column.canSort ? "r-table__column-title--sortable" : ""}`}
                                            >
                                                {column.render("Header")}
                                                {column.canSort && (
                                                    <span className="r-table__sort-icon">
                                                        <i
                                                            className={iconClass(
                                                                column,
                                                                "desc",
                                                                "r-table__sort-icon--down",
                                                            )}
                                                        />
                                                        <i
                                                            className={iconClass(
                                                                column,
                                                                "asc",
                                                                "r-table__sort-icon--up",
                                                            )}
                                                        />
                                                    </span>
                                                )}
                                            </div>
                                            {!isPrintForm && column.filterable && (
                                                <div className="r-table__column-filter">{column.render("Filter")}</div>
                                            )}
                                            {column.canResize && (
                                                <div
                                                    {...column.getResizerProps()}
                                                    className={`r-table__resizer ${column.isResizing ? "isResizing" : ""}`}
                                                />
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        );
                    })}
                </div>
                <div className="r-table__tbody" {...getTableBodyProps()}>
                    {page.length === 0 ? (
                        <div className="rt-noData">Нет данных для отображения</div>
                    ) : isPrintForm ||
                      state.groupBy.length === 0 ||
                      [
                          REPORT_TYPES.ExcessMileageReport,
                          REPORT_TYPES.LoadUnloadTimeReport,
                          REPORT_TYPES.AccountsReceivableReport,
                          REPORT_TYPES.DriversReport,
                      ].includes(props.id) ? (
                        page.map((row, i) => rowItem(row, i))
                    ) : (
                        <VariableSizeList
                            ref={listEl}
                            style={{ overflowX: "hidden" }}
                            height={tbodyHeight}
                            itemCount={page.length}
                            itemSize={getItemSize}
                            width={tbodyWidth}
                            itemKey={(index, data) => data[index].id}
                            itemData={page}
                        >
                            {RenderRow}
                        </VariableSizeList>
                    )}
                </div>
                {!withoutFooter && (
                    <div className="r-table__tfoot">
                        {footerGroups.map((group, i) => {
                            const { key: footerGroupKey, props: footerGroupProps } = splitKeyFromProps(
                                group.getFooterGroupProps(),
                            );
                            return (
                                <div key={footerGroupKey ?? i} className="tr" {...footerGroupProps}>
                                    {group.headers.map((column, i) => {
                                        const footerProps = column.getHeaderProps(column.cellAdditionalProps);
                                        footerProps.className = cn("td", {
                                            [footerProps.className]: !!footerProps.className,
                                            "position-sticky zi-9": column.sticky,
                                        });
                                        const { key: footerKey, props: footerPropsWithoutKey } =
                                            splitKeyFromProps(footerProps);
                                        return (
                                            <div
                                                key={footerKey ?? i}
                                                className="td"
                                                {...footerPropsWithoutKey}
                                                style={{
                                                    ...footerPropsWithoutKey.style,
                                                    ...(column.sticky
                                                        ? {
                                                              left: `${footerGroups[0].headers
                                                                  .slice(0, i)
                                                                  .reduce(
                                                                      (sum, column) => sum + (column.width || 0),
                                                                      0,
                                                                  )}px`,
                                                          }
                                                        : {}),
                                                }}
                                            >
                                                {column.render("Footer")}
                                            </div>
                                        );
                                    })}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
            {!isPrintForm && !withoutPagination && <Pagination isNotExportable {...paginationProps} />}
        </>
    );
};

export default Table;
